import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetPatients } from "~/hooks/useGetPatients";
import { Button, SearchableMultiSelect } from "~/ui";
import { Input } from "~/ui/Input";
import { Modal } from "~/ui/Modal";
import { Select } from "~/ui/Select";

export interface FirmwareModalFormData {
  version: string;
  file?: FileList;
  privacy_mode: "PUBLIC" | "PRIVATE";
  private_members: { id: number; name: string; surname: string }[];
}

interface FirmwareModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "upload" | "edit";
  defaultValues?: Partial<FirmwareModalFormData>;
  onSubmit: (data: FirmwareModalFormData) => void;
  submitLabel?: string;
  isPending?: boolean;
}

export function FirmwareModal({
  isOpen,
  onClose,
  mode,
  defaultValues = {},
  onSubmit,
  submitLabel,
  isPending = false,
}: FirmwareModalProps) {
  const { t } = useTranslation();
  const [membersSearch, setMembersSearch] = useState("");
  const [debouncedMembersSearch, setDebouncedMembersSearch] = useState("");

  // Debounce the search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMembersSearch(membersSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [membersSearch]);

  const { patientsData, isLoadingPatients } = useGetPatients(
    1,
    null,
    debouncedMembersSearch,
    null,
    "active",
    debouncedMembersSearch ? 50 : 10,
  );
  const patients = patientsData?.data ?? [];

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm<FirmwareModalFormData>({
    defaultValues: {
      version: defaultValues.version ?? "",
      privacy_mode: defaultValues.privacy_mode ?? "PUBLIC",
      private_members: defaultValues.private_members ?? [],
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        version: defaultValues.version ?? "",
        privacy_mode: defaultValues.privacy_mode ?? "PUBLIC",
        private_members: defaultValues.private_members ?? [],
      });
    }
    setMembersSearch("");
  }, [isOpen]);

  const privacyMode = watch("privacy_mode");

  // Reset search when privacy mode changes to PUBLIC
  useEffect(() => {
    if (privacyMode === "PUBLIC") {
      setMembersSearch("");
    }
  }, [privacyMode]);

  const handleFormSubmit = (data: FirmwareModalFormData) => {
    // For edit mode, file may be optional
    if (mode === "edit" && !data.file) {
      onSubmit({ ...data, file: undefined });
    } else {
      onSubmit(data);
    }
  };

  const handleClose = () => {
    reset({
      version: "",
      privacy_mode: "PUBLIC",
      private_members: [],
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        mode === "edit"
          ? t("firmware.edit_firmware_version", "Edit Version")
          : t("firmware.upload_firmware", "Upload Version")
      }
      size="lg"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Version Field */}
        <div className="flex flex-col gap-2">
          <Input
            id="version"
            label={t("firmware.version", "Version")}
            error={errors.version?.message}
            {...register("version", {
              required: t("firmware.required", "This field is required"),
            })}
            className="w-full disabled:bg-neutral-100 disabled:text-neutral-400 dark:disabled:bg-neutral-800 dark:disabled:text-neutral-600"
            disabled={mode === "edit"}
          />
        </div>

        {/* File Field (optional in edit mode) */}
        <div className="flex flex-col gap-2">
          <Input
            id="file"
            type="file"
            label={t("firmware.fileize", "File")}
            error={errors.file?.message}
            {...register("file", {
              required:
                mode === "upload"
                  ? t("firmware.required", "This field is required")
                  : false,
            })}
            className="w-full cursor-pointer"
          />
        </div>

        {/* Privacy Field */}
        <div className="flex flex-col gap-2">
          <Select
            id="privacy_mode"
            label={t("firmware.privacy_label", "Privacy Mode")}
            error={errors.privacy_mode?.message}
            {...register("privacy_mode", {
              required: t("firmware.required", "This field is required"),
            })}
            className="w-full cursor-pointer"
          >
            <option value="PUBLIC">
              {t("firmware.privacy_public", "Public")}
            </option>
            <option value="PRIVATE">
              {t("firmware.privacy_private", "Private")}
            </option>
          </Select>
        </div>

        {/* Members Combobox */}
        {privacyMode === "PRIVATE" && (
          <div className="flex flex-col gap-2">
            <Controller
              name="private_members"
              control={control}
              rules={{
                required: t("firmware.required", "This field is required"),
              }}
              render={({ field }) => (
                <SearchableMultiSelect
                  id="members"
                  label={t("firmware.members_label", "Members")}
                  value={field.value.map((member) => member.id)}
                  onChange={(selectedIds: number[]) => {
                    const selectedMembers = selectedIds
                      .map((id) => {
                        const patient = patients.find((p) => p.id === id);
                        if (patient) {
                          return {
                            id: patient.id,
                            name: patient.name,
                            surname: patient.surname,
                          };
                        }
                        const existingMember = field.value.find(
                          (member) => member.id === id,
                        );
                        return existingMember ?? null;
                      })
                      .filter(
                        (
                          member,
                        ): member is {
                          id: number;
                          name: string;
                          surname: string;
                        } => member !== null,
                      );

                    field.onChange(selectedMembers);
                  }}
                  options={patients.map((patient) => ({
                    id: patient.id,
                    label: `${patient.name} ${patient.surname}`,
                  }))}
                  placeholder={t("firmware.members_label", "Select Members")}
                  onSearch={setMembersSearch}
                  isLoading={isLoadingPatients}
                  emptyMessage={t(
                    "firmware.no_members",
                    "No members available",
                  )}
                  showSelectedAsBadges={true}
                />
              )}
            />
            {errors.private_members && (
              <p className="mt-1 text-sm text-error-60">
                {errors.private_members.message}
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            color="neutral"
            onClick={onClose}
            disabled={isPending}
            text={t("firmware.cancel", "Cancel")}
          />
          <Button
            type="submit"
            variant="filled"
            color="primary"
            disabled={isPending}
            text={
              submitLabel ??
              (mode === "edit"
                ? t("firmware.save_changes", "Save Changes")
                : t("firmware.upload", "Upload Firmware"))
            }
          />
        </div>
      </form>
    </Modal>
  );
}
